import { type Money } from "./application";

export enum PropertyKind {
  MultiFamily = "multi_family",
  SingleFamily = "single_family",
  ManufacturedHousing = "manufactured_housing"
}

export const propertyKindLabel: Record<PropertyKind, string> = {
  [PropertyKind.SingleFamily]: "Single family",
  [PropertyKind.MultiFamily]: "Multi family",
  [PropertyKind.ManufacturedHousing]: "Manufactured housing"
};

export enum MediaAttachmentResolution {
  Full = "full",
  Large = "large",
  Medium = "medium",
  Small = "small"
}
interface MediaAttachment {
  global_id: string;
  url: string;
  content_type: string | null;
  original_filename: string | null;
  checksum: string | null;
  extension: string;
  size: number;
  resolution: MediaAttachmentResolution;
}

export interface MediaItem {
  id: number;
  position: number;
  is_cover: boolean;
  created_at: string;
  updated_at: string | null;
  attachments?: MediaAttachment[];
}

export interface ListingPlan {
  beds: number;
  sqft: number;
  baths: number;
}

export interface ImageVariantUrl {
  full: string;
  large?: string;
  medium?: string;
  small: string;
}

interface Amenity {
  key: string;
  level: "property" | "unit";
  title: string;
}

export type ListingPageFilterKey =
  | "price"
  | "beds"
  | "baths"
  | "pets"
  | "listing_type"
  | "state"
  | "city"
  | "move_in_date"
  | "specific_properties"
  | "map";

export type ListingPageDefaultSort =
  | "default"
  | "price_asc"
  | "price_desc"
  | "distance";

export type ListingPageCardFieldKey =
  | "property_name"
  | "price"
  | "beds_baths_sqft"
  | "pets_badge"
  | "photo_carousel"
  | "listing_name";

export type ListingPageCardActionButtonKey =
  | "apply_now"
  | "contact_us"
  | "request_showing";

export interface ListingPageFilterSetting {
  key: ListingPageFilterKey;
  enabled: boolean;
}

export interface ListingPageCardActionButtonSetting {
  key: ListingPageCardActionButtonKey;
  enabled: boolean;
  primary: boolean;
}

export interface ListingPageConfig {
  filters: ListingPageFilterSetting[];
  default_sort: ListingPageDefaultSort;
  pinned_property_ids: number[];
  card_fields: Record<ListingPageCardFieldKey, boolean>;
  card_action_buttons: ListingPageCardActionButtonSetting[];
  reuse_identity_verification: boolean;
}

export const LISTING_PAGE_FILTER_ORDER: ListingPageFilterKey[] = [
  "listing_type",
  "specific_properties",
  "price",
  "city",
  "state",
  "beds",
  "baths",
  "pets",
  "move_in_date",
  "map"
];

export const LISTING_PAGE_CARD_FIELD_KEYS: ListingPageCardFieldKey[] = [
  "property_name",
  "price",
  "beds_baths_sqft",
  "pets_badge",
  "photo_carousel",
  "listing_name"
];

export const LISTING_PAGE_CARD_ACTION_BUTTON_ORDER: ListingPageCardActionButtonKey[] =
  ["apply_now", "contact_us", "request_showing"];

export const LISTING_PAGE_CARD_ACTION_BUTTONS_MAX_ENABLED = 2;

export const LISTING_PAGE_MAX_PINNED_PROPERTIES = 50;

export const DEFAULT_LISTING_PAGE_CONFIG: ListingPageConfig = {
  filters: LISTING_PAGE_FILTER_ORDER.map((key) => ({ key, enabled: true })),
  default_sort: "default",
  pinned_property_ids: [],
  card_fields: {
    property_name: true,
    price: true,
    beds_baths_sqft: true,
    pets_badge: true,
    photo_carousel: true,
    listing_name: true
  },
  card_action_buttons: LISTING_PAGE_CARD_ACTION_BUTTON_ORDER.map((key) => ({
    key,
    enabled: key === "apply_now",
    primary: key === "apply_now"
  })),
  reuse_identity_verification: false
};

const DEFAULT_SORT_VALUES: ListingPageDefaultSort[] = [
  "default",
  "price_asc",
  "price_desc",
  "distance"
];

export const normalizeListingPageConfig = (
  raw?: Partial<ListingPageConfig> | null
): ListingPageConfig => {
  if (!raw) return DEFAULT_LISTING_PAGE_CONFIG;

  const rawFilters = Array.isArray(raw.filters) ? raw.filters : [];
  const seen = new Set<ListingPageFilterKey>();
  const filters: ListingPageFilterSetting[] = [];
  rawFilters.forEach((filter) => {
    const key = filter?.key;
    if (LISTING_PAGE_FILTER_ORDER.includes(key) && !seen.has(key)) {
      seen.add(key);
      filters.push({ key, enabled: Boolean(filter.enabled) });
    }
  });
  LISTING_PAGE_FILTER_ORDER.forEach((key) => {
    if (!seen.has(key)) filters.push({ key, enabled: true });
  });

  const defaultSort = DEFAULT_SORT_VALUES.includes(
    raw.default_sort as ListingPageDefaultSort
  )
    ? (raw.default_sort as ListingPageDefaultSort)
    : "default";

  const rawCardFields: Partial<Record<ListingPageCardFieldKey, boolean>> =
    raw.card_fields ?? {};
  const cardFields = {} as Record<ListingPageCardFieldKey, boolean>;
  LISTING_PAGE_CARD_FIELD_KEYS.forEach((key) => {
    const value = rawCardFields[key];
    cardFields[key] = value === undefined ? true : Boolean(value);
  });

  const pinnedPropertyIds = Array.isArray(raw.pinned_property_ids)
    ? raw.pinned_property_ids.filter(
        (id): id is number => typeof id === "number"
      )
    : [];

  const cardActionButtons = normalizeCardActionButtons(raw.card_action_buttons);

  const reuseIdentityVerification = Boolean(raw.reuse_identity_verification);

  return {
    filters,
    default_sort: defaultSort,
    pinned_property_ids: pinnedPropertyIds,
    card_fields: cardFields,
    card_action_buttons: cardActionButtons,
    reuse_identity_verification: reuseIdentityVerification
  };
};

const normalizeCardActionButtons = (
  raw?: ListingPageCardActionButtonSetting[] | null
): ListingPageCardActionButtonSetting[] => {
  if (!Array.isArray(raw))
    return LISTING_PAGE_CARD_ACTION_BUTTON_ORDER.map((key) => ({
      key,
      enabled: key === "apply_now",
      primary: key === "apply_now"
    }));

  const seen = new Set<ListingPageCardActionButtonKey>();
  const ordered: ListingPageCardActionButtonSetting[] = [];
  let primaryKeyPresent = false;
  raw.forEach((button) => {
    const key = button?.key;
    if (LISTING_PAGE_CARD_ACTION_BUTTON_ORDER.includes(key) && !seen.has(key)) {
      seen.add(key);
      if (button && Object.prototype.hasOwnProperty.call(button, "primary"))
        primaryKeyPresent = true;
      ordered.push({
        key,
        enabled: Boolean(button.enabled),
        primary: Boolean(button.primary)
      });
    }
  });
  LISTING_PAGE_CARD_ACTION_BUTTON_ORDER.forEach((key) => {
    if (!seen.has(key)) ordered.push({ key, enabled: false, primary: false });
  });

  // Pre-feature configs have no `primary` key at all — backfill Apply Now.
  if (!primaryKeyPresent) {
    ordered.forEach((button) => {
      if (button.key === "apply_now") button.primary = button.enabled;
    });
  }

  let enabledCount = 0;
  let primaryAssigned = false;
  const settled = ordered.map((button) => {
    const enabled =
      button.enabled &&
      enabledCount < LISTING_PAGE_CARD_ACTION_BUTTONS_MAX_ENABLED;
    if (enabled) enabledCount += 1;

    const primary = enabled && button.primary && !primaryAssigned;
    if (primary) primaryAssigned = true;

    return { key: button.key, enabled, primary };
  });

  // Exactly one primary whenever any button is enabled: a card never shows two
  // coloured CTAs, and never shows only muted ones either.
  if (!primaryAssigned) {
    const firstEnabled = settled.find((button) => button.enabled);
    if (firstEnabled) firstEnabled.primary = true;
  }

  return settled;
};

export interface PartnerDetails {
  apply_token: string;
  amenities: {
    property: Amenity[];
    unit: Amenity[];
  };
  name: string;
  slug: string;
  url: string;
  asset_logo_url: string | null;
  // False when the partner is flagged crm_listing_sitemap_excluded: an internal
  // or demo account whose listings pages must not be indexed. Optional because
  // an API deployed behind the client simply omits it, and absent must mean
  // "indexable" — the current behaviour — never "noindex the whole site".
  search_indexable?: boolean;
  colors?: {
    primary: Record<string, string>;
    background?: Record<string, string>;
  };
  phone_number: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone_number: string;
  email: string;
  is_english_only?: boolean;
  home_site_rent?: boolean;
  sites_and_communities?: boolean;
  property_type?: PropertyKind | null;
  // Partner's own GA4 Measurement ID(s) (Portal → Settings → Integration →
  // Google Analytics). Free-text setting; the portal stores an array, but a
  // legacy single string is also possible. Absent/empty means GA4 is disabled.
  google_analytics_id?: string | string[] | null;
  listing_page_config?: ListingPageConfig;
  // Google Ads conversion tracking (Portal → Settings → Integration → Google
  // Ads). Conversion ID (e.g. "AW-123456789") plus one label per conversion
  // goal. Absent/empty means Ads conversion reporting is disabled (CRM-952).
  google_ads_conversion_id?: string | null;
  google_ads_contact_form_conversion_label?: string | null;
  google_ads_showing_conversion_label?: string | null;
}

export interface PropertyDetails {
  name: string;
  slug: string;
  primary_image_url: ImageVariantUrl | null;
  kind: PropertyKind;
  address: {
    address1: string;
    one_line_address: string;
    city: string;
    state_or_province: string;
    postal_code: string;
    country: string;
  };
  branding: {
    enabled?: boolean;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    logo?: {
      value: string;
      asset: {
        url: string;
        content_type: string;
        filename: string;
        size: number;
        extension: string;
      };
    };
    palette?: Record<string, string>;
  };
  custom_button_url: string | null;
}

export type RentDisplayMode = "both" | "home" | "site" | "none";

export type ListingPricePrimaryDisplay = "sale_price" | "home_rent" | "hide";
export type ListingPriceSecondaryDisplay = "site_rent" | "hide";

export interface MapPin {
  slug: string;
  lat: number;
  lng: number;
  unit_url: string;
  title: string;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary: Money | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  occupied: boolean;
  available_at: string | null;
  waitlist: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  dog_size: DogSize | null;
  property_name: string;
  property_address: string;
}

export type DogSize = "small" | "large_and_small";

export type SecurityDepositKind = "first_month_rent" | "amount";

export interface ListingAdditionalFees {
  application_fee: {
    enabled: boolean;
    amount: Money | null;
  };
  security_deposit: {
    enabled: boolean;
    kind: SecurityDepositKind | null;
    amount: Money | null;
  };
  other_fees: {
    enabled: boolean;
    text: string | null;
  };
}

interface BaseListing {
  id: number;
  slug: string;
  partner_slug: string;
  title: string;
  description: string | null;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_secondary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  available_at: string | null;
  occupied: boolean;
  waitlist: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  dog_size: DogSize | null;
  city: string;
  state_or_province: string;
  postal_code: string;
  unit_url: string;
  lat: number | null;
  lng: number | null;
  media: MediaItem[];
  property: PropertyDetails;
  amenities: {
    id: number;
    title: string;
  }[];
  // CRM-1167, unit-sourced marketing lists from the PMS. `null` means the PMS
  // never supplied the field — same rendering as an empty list (no section),
  // but the two are kept distinct on the wire.
  lease_terms: string[] | null;
  included_utilities: string[] | null;
  show_apply_now_button: boolean;
  show_contact_us_button: boolean;
  show_request_showing_button: boolean;
  showing_scheduling_enabled: boolean;
  custom_button_url: string | null;
  additional_fees: ListingAdditionalFees | null;
  // Unit-sourced fees (AppFolio), integer minor units (cents), nullable. When
  // present these take precedence over the Listing-template `additional_fees`
  // toggles on the public listing page.
  application_fee_cents: number | null;
  security_deposit_cents: number | null;
}

interface WithML {
  can_apply: true;
  magic_link: {
    id: string;
    monthly_rent_amount: Money;
    created_at: string;
    updated_at: string;
    link: string;
    active: boolean;
    name: string | null;
    email: string | null;
    unit_id: string;
    property_id: string;
    application_id: string | null;
    kind: "unit";
  };
}

interface WithoutML {
  can_apply: false;
}

export type Listing = BaseListing & (WithML | WithoutML);

export enum QuestionType {
  YesNo = "yes_no",
  MultipleChoice = "multiple_choice",
  PlainText = "plain_text"
}

export interface PrescreeningQuestion {
  id: string;
  type: QuestionType;
  name: string;
  question: string;
  required: boolean;
  order: number;
  // Stable value to submit back — always English, never localized.
  options?: string[];
  // Display text for `options`, positionally matched and already resolved to
  // the current locale server-side. Display-only, never submit from this.
  option_labels?: string[];
  // Conditional (follow-up) question linkage. When both fields are populated
  // this entry is a conditional child: it is only shown to applicants once its
  // parent question is answered with `parent_answer`. Both are absent for
  // top-level questions. Snake_case matches the wire format produced/accepted
  // by boompay-api (the API traffic is not camelized in either client app).
  parent_question_id?: string;
  parent_answer?: string;
}

export enum PrescreeningMode {
  Range = "ranges",
  Numeric = "numeric"
}
export interface ListingButtons {
  show_apply_now_button: boolean;
  show_request_showing_button: boolean;
  show_contact_us_button: boolean;
  show_custom_button: boolean;
}
export interface PrescreeningQuestionRecord {
  id: number;
  prescreening_template_identifier: string;
  created_at: string;
  updated_at: string;
}
export type PrescreeningCreditScore = PrescreeningQuestionRecord & {
  score: number | null;
  score_range: string | null;
  score_unspecified?: boolean | null;
};
export type PrescreeningGrossMonthlyIncome = PrescreeningQuestionRecord & {
  income_cents: number | null;
  income_range: string | null;
};
